import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Boxes, List, Settings2, Shapes } from "lucide-react";
import { Modal } from "../../../../shared/ui/modal";

type Props = {
    isOpen: boolean;
    onClose: () => void;
    onOpenBoard?: () => void;
    onShowSessions?: () => void;
    onShowScenes?: () => void;
    onShowDefaults?: () => void;
    onManageFigures?: () => void;
};

// Constellation home options. Open a board (patient or free), browse past
// sessions / saved scenes, manage reusable figures, or set board defaults.
export default function ConstellationLaunchSheet({
    isOpen,
    onClose,
    onOpenBoard,
    onShowSessions,
    onShowScenes,
    onShowDefaults,
    onManageFigures,
}: Props) {
    const { t } = useTranslation();

    const go = (fn?: () => void) => () => {
        onClose();
        fn?.();
    };

    const entry = (
        onClick: () => void,
        icon: React.ReactNode,
        title: string,
        hint: string,
        primary = false,
    ) => (
        <button
            type="button"
            onClick={onClick}
            className={`mb-4 flex w-full items-center gap-3 rounded-2xl border p-3 text-left transition-colors ${
                primary
                    ? "border-brand-100 bg-brand-50 text-brand-700 hover:bg-brand-100 dark:border-brand-500/30 dark:bg-brand-500/10 dark:text-brand-300"
                    : "border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800/50"
            }`}
        >
            <span
                className={`flex h-10 w-10 items-center justify-center rounded-full ${
                    primary
                        ? "bg-white text-brand-600 shadow-sm dark:bg-gray-800"
                        : "bg-gray-100 text-gray-500 dark:bg-gray-800"
                }`}
            >
                {icon}
            </span>
            <span className="flex-1">
                <span className="block text-sm font-semibold">{title}</span>
                <span className="block text-xs opacity-80">{hint}</span>
            </span>
        </button>
    );

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            showCloseButton={false}
            className="mx-4 max-w-lg rounded-3xl"
        >
            <motion.div
                initial={{ y: 24, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
                className="flex max-h-[80vh] flex-col p-5"
            >
                <div className="mb-4">
                    <h2 className="text-lg font-bold text-gray-800 dark:text-white">
                        {t("constellation.launchSheet.title")}
                    </h2>
                    <p className="text-sm text-content-tertiary">
                        {t("constellation.launchSheet.subtitle")}
                    </p>
                </div>

                {onOpenBoard &&
                    entry(
                        go(onOpenBoard),
                        <Boxes className="h-5 w-5" />,
                        t("constellation.launchSheet.anonymousTitle"),
                        t("constellation.launchSheet.openBoardHint"),
                        true,
                    )}

                {onShowSessions &&
                    entry(
                        go(onShowSessions),
                        <List className="h-5 w-5" />,
                        t("constellation.launchSheet.viewSessions"),
                        t("constellation.launchSheet.viewSessionsHint"),
                    )}

                {onShowScenes &&
                    entry(
                        go(onShowScenes),
                        <Boxes className="h-5 w-5" />,
                        t("constellation.scenes.saved"),
                        t("constellation.scenes.hint"),
                    )}

                {onManageFigures &&
                    entry(
                        go(onManageFigures),
                        <Shapes className="h-5 w-5" />,
                        t("constellation.figureCatalog.manage"),
                        t("constellation.figureCatalog.manageHint"),
                    )}

                {onShowDefaults &&
                    entry(
                        go(onShowDefaults),
                        <Settings2 className="h-5 w-5" />,
                        t("constellation.boardDefaults.title"),
                        t("constellation.boardDefaults.hint"),
                    )}

                <button
                    type="button"
                    onClick={onClose}
                    className="mt-2 w-full rounded-xl border border-gray-200 py-2.5 text-sm font-semibold text-gray-500 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800/50"
                >
                    {t("common.cancel")}
                </button>
            </motion.div>
        </Modal>
    );
}
